/**
 * 飞书认证 React Context Provider
 *
 * 包装整个工作台应用，提供认证状态 + 权限画像。
 * 登录后自动从 /auth/profile 获取角色、权限、可见工作台信息。
 *
 * 增强：错误降级 UI、全局错误监听
 */
import { createContext, useContext, useEffect, type ReactNode } from 'react'
import axios from 'axios'
import { useFeishuAuth, clearAuthStorage } from './hooks/useFeishuAuth'
import {
  DEV_BYPASS_LOGOUT_SESSION_KEY,
  isDevBypassLogoutSession,
  clearDevBypassLogoutSession,
  consumeDevBypassResumeFromUrl,
  isViteDevAuthBypassEnabled,
} from './devBypass'
import { useAuthProfile, type AuthProfile } from './hooks/useAuthProfile'
import type { FeishuAuthConfig, FeishuUser, AuthErrorType } from './auth'

interface FeishuContextValue {
  user: FeishuUser | null
  token: string | null
  loading: boolean
  error: string | null
  errorType: AuthErrorType | null
  login: () => void
  logout: () => void
  isAuthenticated: boolean
  profile: AuthProfile | null
  profileLoading: boolean
  refetchProfile: () => void
  isAdmin: boolean
  hasPermission: (code: string) => boolean
  hasAnyPermission: (codes: string[]) => boolean
  hasAllPermissions: (codes: string[]) => boolean
  hasRole: (name: string) => boolean
  hasAnyRole: (names: string[]) => boolean
  canAccessWorkbench: (wb: string) => boolean
  isMenuVisible: (workbench: string, menuKey: string) => boolean
  canSeeMenu: (workbench: string, menuKey: string, permissions?: string[]) => boolean
  /**
   * 获取指定工作台的模式配置
   * 返回 'blank' | 'pilot' | 'full'，无配置时默认返回 'full'
   */
  getWorkstationMode: (workstation: string) => string
}

const FeishuContext = createContext<FeishuContextValue | null>(null)

interface FeishuAuthProviderProps {
  config: FeishuAuthConfig
  children: ReactNode
  loginFallback?: ReactNode
  loadingFallback?: ReactNode
}

const alwaysTrue = () => true

/** 开发环境认证旁路（每帧读取；Vite 在构建时内联 import.meta.env） */
const viteDevBypass = () => isViteDevAuthBypassEnabled()

const DEV_MOCK_USER: FeishuUser = {
  id: 0,
  name: '开发测试用户',
  email: 'dev@cnkis.local',
  avatar: '',
}

function isAuthDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('cnkis_auth_debug') === '1') return true
    if (window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.split('?')[1] || ''
      const hashParams = new URLSearchParams(hashQuery)
      if (hashParams.get('cnkis_auth_debug') === '1') return true
    }
  } catch {
    // ignore
  }
  try {
    if ((window as any).__CNKIS_AUTH_DEBUG__ === true) return true
  } catch {
    // ignore
  }
  try {
    return localStorage.getItem('cnkis_auth_debug') === '1'
  } catch {
    return false
  }
}

function workstationModeFromProfile(
  profile: AuthProfile | null | undefined,
  workstation: string,
): string {
  const modes = profile?.workstation_modes
  if (!modes) return 'full'
  return modes[workstation] ?? 'full'
}

type AuthProfileHookReturn = ReturnType<typeof useAuthProfile>

function canSeeMenuFromProfile(
  authProfile: AuthProfileHookReturn,
  workbench: string,
  menuKey: string,
  permissions: string[] = [],
): boolean {
  // 勿在 loading 时 return true：刷新会 setLoading(true)，会短时「全菜单可见」导致人员技能等项闪现
  if (!authProfile.profile) {
    if (!permissions.length) return true
    return false
  }
  const wbMenus = authProfile.profile.visible_menu_items?.[workbench] || []
  const hasWorkbenchMenuConfig = Object.prototype.hasOwnProperty.call(
    authProfile.profile.visible_menu_items || {},
    workbench,
  )
  const hasProfileSignals =
    wbMenus.length > 0
    || (authProfile.profile.permissions?.length || 0) > 0
  if (!hasProfileSignals) return true
  // 无该 workbench 的菜单配置时，旧逻辑「一律放行」会导致短时展示全部侧栏项，与 PermissionGuard / API 403 不一致
  if (!hasWorkbenchMenuConfig) {
    if (!permissions.length) return true
    return authProfile.hasAnyPermission(permissions)
  }
  if (authProfile.isMenuVisible(workbench, menuKey)) return true
  // 研究台：侧栏已合并为 study-info「研究全链路」；历史角色仅勾选 project-full-link 时仍应显示
  if (
    workbench === 'research' &&
    menuKey === 'study-info' &&
    authProfile.isMenuVisible(workbench, 'project-full-link')
  ) {
    return true
  }
  if (!wbMenus.length && permissions.length && !authProfile.hasAnyPermission(permissions)) return false
  if (!permissions.length) return true
  const allowed = authProfile.hasAnyPermission(permissions)
  if (!allowed && isAuthDebugEnabled()) {
    console.warn('[CNKIS auth] menu denied', {
      workbench,
      menuKey,
      permissions,
      wbMenus,
      profilePermissionsCount: authProfile.profile.permissions?.length || 0,
      roleNames: (authProfile.profile.roles || []).map((role) => role.name),
      username: authProfile.profile.username,
    })
  }
  return allowed
}

/** 从门户页接收 token 的 postMessage 类型（跨工作台同源/同主机免二次登录） */
const CNKIS_PORTAL_READY = 'cnkis_portal_ready'
const CNKIS_TOKEN_HANDOFF = 'cnkis_token_handoff'

interface ProfileAuthRequiredPanelProps {
  message: string
  onLogout: () => void
  onRetry: () => void
}

/**
 * /auth/profile 返回 401 时展示：不再自动 logout→autoLogin，避免飞书 OAuth 死循环（本地库无账号、JWT 无效等场景）。
 */
function ProfileAuthRequiredPanel({ message, onLogout, onRetry }: ProfileAuthRequiredPanelProps) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: '#f8fafc', padding: 24,
    }}
    >
      <div style={{
        maxWidth: 440, textAlign: 'center',
        background: '#fff', borderRadius: 12, padding: 28,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
      >
        <h2 style={{ fontSize: 18, margin: '0 0 12px', color: '#0f172a' }}>登录状态异常</h2>
        <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: '0 0 20px' }}>{message}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
          <button
            type="button"
            onClick={onRetry}
            style={{
              padding: '10px 18px', fontSize: 14, borderRadius: 8, border: '1px solid #cbd5e1',
              background: '#fff', color: '#334155', cursor: 'pointer',
            }}
          >
            重新拉取权限
          </button>
          <button
            type="button"
            onClick={onLogout}
            style={{
              padding: '10px 18px', fontSize: 14, borderRadius: 8, border: 'none',
              background: '#059669', color: '#fff', cursor: 'pointer',
            }}
          >
            退出并重新登录
          </button>
        </div>
      </div>
    </div>
  )
}

/** 问卷扫码测试 H5：不经过飞书 OAuth；提交对象展示名取自 user.name */
const PLAYTEST_ANONYMOUS_FEISHU_CONTEXT: FeishuContextValue = {
  user: { id: 0, name: '扫码测试', email: '', avatar: '' },
  token: null,
  loading: false,
  error: null,
  errorType: null,
  login: () => {},
  logout: () => {},
  isAuthenticated: false,
  profile: null,
  profileLoading: false,
  refetchProfile: async () => {},
  isAdmin: false,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  hasRole: () => false,
  hasAnyRole: () => false,
  canAccessWorkbench: () => false,
  isMenuVisible: () => false,
  canSeeMenu: () => false,
  getWorkstationMode: () => 'full',
}

export function FeishuPlaytestAnonymousProvider({ children }: { children: ReactNode }) {
  return (
    <FeishuContext.Provider value={PLAYTEST_ANONYMOUS_FEISHU_CONTEXT}>
      {children}
    </FeishuContext.Provider>
  )
}

export function FeishuAuthProvider({
  config,
  children,
  loginFallback,
  loadingFallback,
}: FeishuAuthProviderProps) {
  const auth = useFeishuAuth(config)
  // DEV_BYPASS 时传 null 给 useAuthProfile，跳过后端 /auth/profile 调用，
  // 避免用 dev-bypass-token 请求被 403 后触发 auth.logout() 导致白屏循环。
  const devBypass = viteDevBypass()
  const authProfile = useAuthProfile(devBypass ? null : auth.token, config.apiBaseUrl || '/api/v1')

  // 从秘书台门户打开时：若本页无 token 且由门户打开，向 opener 请求 token 并写入本地，避免重复登录
  useEffect(() => {
    if (typeof window === 'undefined' || !window.opener) return
    if (localStorage.getItem('auth_token')) return
    const handler = (e: MessageEvent) => {
      if (e.data?.type !== CNKIS_TOKEN_HANDOFF) return
      try {
        if (new URL(e.origin).hostname !== window.location.hostname) return
      } catch {
        return
      }
      const { token, tokenTs, authUser } = e.data
      if (token) {
        try {
          localStorage.setItem('auth_token', token)
          if (tokenTs) localStorage.setItem('auth_token_ts', String(tokenTs))
          if (authUser) localStorage.setItem('auth_user', authUser)
        } catch {}
        window.removeEventListener('message', handler)
        window.location.reload()
      }
    }
    window.addEventListener('message', handler)
    try {
      window.opener.postMessage({ type: CNKIS_PORTAL_READY }, '*')
    } catch {}
    return () => window.removeEventListener('message', handler)
  }, [])

  // Global error listeners for logging
  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      try {
        const payload = {
          workstation: window.location.pathname.split('/')[1] || 'unknown',
          error_type: 'unhandled_rejection',
          message: event.reason?.message || String(event.reason),
          stack: event.reason?.stack?.slice(0, 2000),
        }
        navigator.sendBeacon?.('/api/v1/log/frontend-error', JSON.stringify(payload))
      } catch { /* best-effort */ }
    }
    const onGlobalError = (event: ErrorEvent) => {
      try {
        const payload = {
          workstation: window.location.pathname.split('/')[1] || 'unknown',
          error_type: 'global_error',
          message: event.message,
          stack: event.error?.stack?.slice(0, 2000),
        }
        navigator.sendBeacon?.('/api/v1/log/frontend-error', JSON.stringify(payload))
      } catch { /* best-effort */ }
    }

    window.addEventListener('unhandledrejection', onUnhandledRejection)
    window.addEventListener('error', onGlobalError)
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
      window.removeEventListener('error', onGlobalError)
    }
  }, [])

  if (devBypass) {
    if (consumeDevBypassResumeFromUrl()) {
      clearDevBypassLogoutSession()
    }
    /** 用户已在开发旁路下点「退出」：不自动写 token，展示登录页；点击登录后清除标记并刷新恢复旁路 */
    if (isDevBypassLogoutSession()) {
      const devLoggedOutValue: FeishuContextValue = {
        user: null,
        token: null,
        loading: false,
        error: null,
        errorType: null,
        login: () => {
          clearDevBypassLogoutSession()
          window.location.reload()
        },
        logout: () => {},
        isAuthenticated: false,
        profile: null,
        profileLoading: false,
        refetchProfile: async () => {},
        isAdmin: false,
        hasPermission: () => false,
        hasAnyPermission: () => false,
        hasAllPermissions: () => false,
        hasRole: () => false,
        hasAnyRole: () => false,
        canAccessWorkbench: () => false,
        isMenuVisible: () => false,
        canSeeMenu: () => false,
        getWorkstationMode: () => 'full',
      }
      return (
        <FeishuContext.Provider value={devLoggedOutValue}>
          {loginFallback ?? <DefaultLoading />}
        </FeishuContext.Provider>
      )
    }

    // 把 dev-bypass-token 写入 localStorage；若残留飞书 JWT 会触发 OAuth 报错，故非旁路 token 时强制覆盖
    try {
      const existing = localStorage.getItem('auth_token')
      if (existing !== 'dev-bypass-token') {
        localStorage.setItem('auth_token', 'dev-bypass-token')
        localStorage.setItem('auth_token_ts', String(Date.now()))
        localStorage.setItem('auth_user', JSON.stringify({ id: 0, name: '开发测试用户', email: 'dev@cnkis.local', avatar: '' }))
      }
    } catch { /* ignore */ }

    const p = authProfile.profile
    const fromBackendAccount = Boolean(p && p.id > 0)
    const devDisplayUser: FeishuUser = p
      ? {
          id: p.id,
          name: (p.display_name || p.username || DEV_MOCK_USER.name).trim() || DEV_MOCK_USER.name,
          email: p.email || DEV_MOCK_USER.email,
          avatar: p.avatar || '',
        }
      : DEV_MOCK_USER

    try {
      if (fromBackendAccount && p) {
        localStorage.setItem(
          'auth_user',
          JSON.stringify({
            id: p.id,
            name: devDisplayUser.name,
            email: devDisplayUser.email,
            avatar: devDisplayUser.avatar,
          }),
        )
      }
    } catch { /* ignore */ }

    const devBypassLogout = () => {
      const apiBase = config.apiBaseUrl || '/api/v1'
      try {
        const t = localStorage.getItem('auth_token')
        if (t) {
          axios.post(`${apiBase}/auth/logout`, {}, { headers: { Authorization: `Bearer ${t}` } }).catch(() => {})
        }
      } catch {
        /* ignore */
      }
      clearAuthStorage()
      try {
        sessionStorage.setItem(DEV_BYPASS_LOGOUT_SESSION_KEY, '1')
      } catch {
        /* ignore */
      }
      window.location.reload()
    }

    // 开发旁路固定带 dev-bypass-token；后端 DEBUG 下 require_permission 对该 token / dev-bypass 账号全放行
    // （见 apps.identity.decorators）。画像若来自真实账号 id>0 但 permissions 未含 equipment.write，勿据此误藏按钮。
    const devValue: FeishuContextValue = {
      user: devDisplayUser,
      token: 'dev-bypass-token',
      loading: false,
      error: null,
      errorType: null,
      login: () => {},
      logout: devBypassLogout,
      isAuthenticated: true,
      profile: p,
      profileLoading: authProfile.loading,
      refetchProfile: authProfile.refetch,
      isAdmin: true,
      hasPermission: alwaysTrue,
      hasAnyPermission: alwaysTrue,
      hasAllPermissions: alwaysTrue,
      hasRole: alwaysTrue,
      hasAnyRole: alwaysTrue,
      canAccessWorkbench: alwaysTrue,
      isMenuVisible: fromBackendAccount ? authProfile.isMenuVisible : alwaysTrue,
      canSeeMenu: fromBackendAccount
        ? (wb, key, perms) => canSeeMenuFromProfile(authProfile, wb, key, perms)
        : () => true,
      getWorkstationMode: (ws) => workstationModeFromProfile(p, ws),
    }
    return (
      <FeishuContext.Provider value={devValue}>
        {children}
      </FeishuContext.Provider>
    )
  }

  const contextValue: FeishuContextValue = {
    ...auth,
    profile: authProfile.profile,
    profileLoading: authProfile.loading,
    refetchProfile: authProfile.refetch,
    // 与 PermissionGuard 放行策略一致：预置 admin/superadmin「角色名」或画像中已含全局 * 权限码（避免 roles 解析异常时超管仍被挡在按钮外）
    isAdmin: authProfile.hasAnyRole(['admin', 'superadmin']) || authProfile.isAdmin,
    hasPermission: authProfile.hasPermission,
    hasAnyPermission: authProfile.hasAnyPermission,
    hasAllPermissions: authProfile.hasAllPermissions,
    hasRole: authProfile.hasRole,
    hasAnyRole: authProfile.hasAnyRole,
    canAccessWorkbench: authProfile.canAccessWorkbench,
    isMenuVisible: authProfile.isMenuVisible,
    getWorkstationMode: (workstation: string): string =>
      workstationModeFromProfile(authProfile.profile, workstation),
    canSeeMenu: (workbench: string, menuKey: string, permissions: string[] = []) =>
      canSeeMenuFromProfile(authProfile, workbench, menuKey, permissions),
  }

  // 仅在 profile 加载成功后再渲染主内容，避免 ClawQuickPanel 等组件在 token 尚未被 /auth/profile 验证前抢跑请求导致 401 级联（同事登录后闪退）
  let content: ReactNode
  if (auth.loading) {
    content = loadingFallback || <DefaultLoading />
  } else if (auth.error) {
    content = <ErrorFallback error={auth.error} errorType={auth.errorType} onRetry={auth.login} />
  } else if (!auth.isAuthenticated && loginFallback) {
    content = loginFallback
  } else if (authProfile.authRequired) {
    content = (
      <ProfileAuthRequiredPanel
        message={authProfile.error || '权限画像校验失败（未授权），请检查账号是否已同步或联系管理员。'}
        onLogout={auth.logout}
        onRetry={() => {
          void authProfile.refetch()
        }}
      />
    )
  } else if (auth.isAuthenticated && (!authProfile.profile) && authProfile.loading) {
    content = loadingFallback || <DefaultLoading />
  } else if (auth.isAuthenticated && auth.token && !authProfile.profile && !authProfile.loading) {
    // /auth/profile 非 401 失败（如 403/5xx）且无可缓存旧画像时，原先无限「正在加载」导致无法进入任何工作台
    if (authProfile.error) {
      content = children
    } else {
      content = loadingFallback || <DefaultLoading />
    }
  } else {
    content = children
  }

  return (
    <FeishuContext.Provider value={contextValue}>
      {content}
    </FeishuContext.Provider>
  )
}

export function useFeishuContext(): FeishuContextValue {
  const ctx = useContext(FeishuContext)
  if (!ctx) {
    throw new Error('useFeishuContext 必须在 FeishuAuthProvider 内使用')
  }
  return ctx
}

function DefaultLoading() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontSize: '14px',
      color: '#64748b',
    }}>
      正在加载...
    </div>
  )
}

function ErrorFallback({ error, errorType, onRetry }: {
  error: string
  errorType: AuthErrorType | null
  onRetry: () => void
}) {
  const hint = errorType === 'network'
    ? '无法连到 API 服务。本机开发时请先启动 Django（默认 8001，与人事台 Vite 代理一致），并确认数据库与 SSH 隧道已就绪。'
    : errorType === 'config'
    ? '应用配置异常，请联系管理员。'
    : '认证失败，请重新登录。'

  const handleClearAndRetry = () => {
    try {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      localStorage.removeItem('auth_profile')
      localStorage.removeItem('auth_roles')
      localStorage.removeItem('auth_workbenches')
      localStorage.removeItem('auth_token_ts')
      localStorage.removeItem('auth_session_meta')
      sessionStorage.removeItem('auth_token')
      sessionStorage.removeItem('auth_user')
      sessionStorage.removeItem('auth_profile')
      sessionStorage.removeItem('auth_roles')
      sessionStorage.removeItem('auth_workbenches')
      sessionStorage.removeItem('auth_token_ts')
      sessionStorage.removeItem('auth_session_meta')
    } catch { /* ignore */ }
    onRetry()
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#f8fafc',
    }}>
      <div style={{
        maxWidth: 400, padding: 32, textAlign: 'center',
        background: '#fff', borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%', margin: '0 auto 16px',
          background: errorType === 'network' ? '#fef3c7' : '#fef2f2',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: errorType === 'network' ? '#d97706' : '#dc2626',
          fontSize: 22, fontWeight: 'bold',
        }}>
          {errorType === 'network' ? '!' : 'x'}
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '0 0 8px' }}>
          {errorType === 'network' ? '网络异常' : '登录失败'}
        </h3>
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 8px', lineHeight: 1.6 }}>
          {hint}
        </p>
        <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 20px', wordBreak: 'break-all' }}>
          {error}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={() => window.location.reload()} style={{
            padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0',
            background: '#fff', color: '#334155', fontSize: 13,
            fontWeight: 500, cursor: 'pointer',
          }}>
            刷新页面
          </button>
          <button onClick={handleClearAndRetry} style={{
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: '#3b82f6', color: '#fff', fontSize: 13,
            fontWeight: 500, cursor: 'pointer',
          }}>
            重新登录
          </button>
        </div>
      </div>
    </div>
  )
}
